(ns ui.view.transaction-process.event
  (:require
   [common.localization :as loc]
   [common.transaction-process.templates.registry :as template-registry]
   [common.util :as cu]
   [frontend.actions.entity :as entity-actions]
   [frontend.conn :as conn]
   [frontend.controller.websocket.event :as ws]
   [frontend.logging :as logging]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.subscription :as subscription]
   [frontend.utils :as fu]
   [promesa.core :as p]
   [re-frame.core :as rf]
   [ui.view.transaction-process.model :as model]))

;; Custom effects

(rf/reg-fx
 ::open-subscription
 (fn [{:keys [process-id on-update-event on-error-event]}]
   (subscription/open-subscription-fx
    {:key                                     ::tp-subscription
     :type                                    :view-subscription/transaction-process
     :opts                                    {:transaction-process-id process-id}
     :on-update-event                         on-update-event
     :on-error-event                          on-error-event
     :close-previous-subscription-if-exists?  true})))

(rf/reg-fx
 ::close-subscription
 (fn [{:keys [on-success]}]
   (subscription/close
    ::tp-subscription
    (fn [_] (when (fn? on-success) (on-success))))))

(rf/reg-fx
 ::field-updates
 ;; Persist a batch of field edits in ONE round-trip via `:entity/update-entities`
 ;; (not one HTTP request per field, as before — that made multi-field draft saves
 ;; crawl). Each entity is updated in its own backend transaction (atomic per
 ;; entity), and that transaction runs the backend transform-write hooks over the
 ;; whole update map at once. Updates are grouped per entity-id; a TP draft save is
 ;; a single entity, so a single transaction.
 (fn [{:keys [updates on-all-done]}]
   (let [serialize      (fn [attr value]
                          (cond
                            (= attr :transaction-process/short-name)
                            (let [trimmed (cu/str-trim value)]
                              (when (cu/non-empty-string? trimmed)
                                trimmed))

                            ;; Blank -> nil so the backend retracts the attribute
                            ;; (matches the prior remove-on-clear behaviour).
                            (and (string? value) (cu/blank-value? value)) nil
                            (and (= attr :transaction-process/team-assignments-edn)
                                 (not (string? value)))                   (pr-str value)
                            :else                                          value))
         entity-updates (->> updates
                             (group-by :entity-id)
                             (mapv (fn [[entity-id items]]
                                     {:db/id entity-id
                                      :data  (into {} (map (fn [{:keys [attr value]}]
                                                             [attr (serialize attr value)]))
                                                   items)})))]
     (if (empty? entity-updates)
       (on-all-done true)
       (entity-actions/*->entities-update!
        entity-updates
        (fn [_] (on-all-done true))
        (fn [err]
          (logging/error "Failed to save fields" err)
          (rf/dispatch [::show-save-error err])
          (on-all-done false)))))))

(rf/reg-fx
 ::disable-entity
 (fn [{:keys [entity-id on-success on-error]}]
   (entity-actions/*->disable!
    entity-id
    (fn [resp] (when on-success (on-success resp)))
    (fn [err] (when on-error (on-error err))))))

(rf/reg-fx
 ::sync-entities-to-session
 (fn [{:keys [entities]}]
   (when (seq entities)
     (fu/manage-entities session/reverse-lookup
                         session/session
                         entities))))

(rf/reg-fx
 ::navigate-to-grid
 (fn [_]
   (routing/set-view! [:transaction-processes-grid])))

(defn- draft-updates
  [process-id draft]
  (mapv (fn [[attr value]]
          {:entity-id process-id
           :attr      attr
           :value     value})
        draft))

(defn- persist-draft-fx
  [{:keys [process-id draft on-all-done]}]
  [::field-updates
   {:updates     (draft-updates process-id draft)
    :on-all-done on-all-done}])

(defn- save-error-message
  [err]
  (or (:message err)
      (loc/tr :an-error-occurred)))

(defn- open-notification!
  [type title]
  (rf/dispatch [:open-notification
                {:title       title
                 :type        type
                 :auto-close? true}]))

(defn- change-stage-fx
  [{:keys [process-id old-stage new-stage ctx]}]
  [::ws/send {:id         :transaction-process/change-stage
              :body       {:process-id process-id
                           :old-stage  old-stage
                           :new-stage  new-stage}
              :on-success [::change-stage-success ctx new-stage]
              :on-error   [::change-stage-error ctx old-stage]}])

;; Events

(rf/reg-event-fx
 ::show-save-error
 [rf/trim-v]
 (fn [_ [err]]
   {:fx [[:dispatch
          [:open-notification
           {:title       (save-error-message err)
            :type        :error
            :auto-close? true}]]]}))

(rf/reg-event-fx
 ::initialize
 [rf/trim-v]
 (fn [{:keys [db]} [{:keys [state-path process-id] :as ctx}]]
   {:db (assoc-in db state-path (model/create-initial-state process-id))
    :fx [[::open-subscription
          {:process-id      process-id
           :on-update-event [::receive-server-update ctx]
           :on-error-event  [::subscription-error ctx]}]]}))

(rf/reg-event-fx
 ::cleanup
 [rf/trim-v]
 (fn [{:keys [db]} [{:keys [state-path] :as _ctx}]]
   {:db (update db :transaction-process dissoc (last state-path))
    :fx [[::close-subscription {}]]}))

(rf/reg-event-fx
 ::receive-server-update
 [rf/trim-v]
 (fn [{:keys [db]} [{:keys [state-path process-id] :as _ctx} update-msg]]
   (let [updates (:entities update-msg)
         tp (->> updates
                 (filter #(= (:db/id %) process-id))
                 (sort-by :common/updated-on)
                 last)
         ;; Guard against ::cleanup having run while an update was in flight.
         state-present? (some? (get-in db state-path))
         server-state-present? (some? (get-in db (model/server-state-full-path state-path)))]
     (cond-> {:fx [[::sync-entities-to-session {:entities updates}]]}
       (and tp state-present?)
       (assoc :db (update-in db state-path
                             (fn [s]
                               (-> s
                                   (assoc :not-found? false)
                                   (assoc :server-state tp)
                                   model/reconcile-after-save))))

       (and state-present? (not tp) (not server-state-present?))
       (assoc :db (assoc-in db (model/not-found-full-path state-path) true))))))

(rf/reg-event-db
 ::subscription-error
 [rf/trim-v]
 (fn [db [{:keys [state-path] :as _ctx} error]]
   (logging/error "Transaction process subscription failed" error)
   (if (get-in db state-path)
     (assoc-in db (model/not-found-full-path state-path) true)
     db)))

(rf/reg-event-db
 ::update-draft-field
 [rf/trim-v]
 (fn [db [{:keys [state-path] :as _ctx} field-kw value]]
   (let [draft-path (model/draft-full-path state-path)
         db'        (assoc-in db (conj draft-path field-kw) value)
         server     (get-in db' (model/server-state-full-path state-path))
         template   (some-> (:transaction-process/template-kw server)
                            template-registry/get-template)
         clears     (when template
                      (model/hidden-field-clears
                       {:template    template
                        :entity-data (merge server (get-in db' draft-path))}))]
     (cond-> db'
       (seq clears) (update-in draft-path merge clears)))))

(rf/reg-event-fx
 ::save-draft
 [rf/trim-v]
 (fn [{:keys [db]} [{:keys [state-path process-id] :as ctx}]]
   (let [draft (get-in db (model/draft-full-path state-path))]
     (if (empty? draft)
       {:fx [[:dispatch [::save-draft-complete ctx true]]]}
       {:db (update-in db state-path model/start-in-flight draft)
        :fx [(persist-draft-fx
              {:process-id  process-id
               :draft       draft
               :on-all-done (fn [success?]
                              (rf/dispatch [::save-draft-complete ctx success?]))})]}))))

(rf/reg-event-db
 ::save-draft-complete
 [rf/trim-v]
 (fn [db [{:keys [state-path] :as _ctx} success?]]
   ;; Draft is not cleared here directly — reconcile-after-save runs per-field
   ;; against server-state. If the backend already had our value (no-op save),
   ;; server-state matches our snapshot at this point and reconcile drops the
   ;; draft entry immediately. Otherwise the subscription push will land soon
   ;; and the receive handler reconciles then. If ::cleanup has fired while
   ;; the save was in flight, state-path is gone; skip.
   (if-not (get-in db state-path)
     db
     (update-in db state-path
                (fn [s]
                  (cond
                    (and success? (:in-flight s))
                    (-> s
                        (assoc :saving? false)
                        (assoc-in [:in-flight :ok?] true)
                        model/reconcile-after-save)

                    success?
                    (assoc s :saving? false)

                    :else
                    ;; Save failed — drop the snapshot so a later unrelated
                    ;; server push doesn't silently drop the user's draft.
                    (-> s
                        (assoc :saving? false)
                        (assoc :in-flight nil))))))))

(rf/reg-event-fx
 ::change-stage
 [rf/trim-v]
 (fn [{:keys [db]} [{:keys [state-path process-id] :as ctx} new-stage field-vals]]
   (let [draft-path (model/draft-full-path state-path)
         db (if (seq field-vals)
              (update-in db draft-path merge field-vals)
              db)
         ;; Always close any open detail/side-panel when we change stages.
         ;; Otherwise the verification or decision detail panel from the prior
         ;; stage stays visible behind the post-transition card layout.
         db (assoc-in db (model/detail-panel-full-path state-path) nil)
         draft (get-in db draft-path)
         old-stage (get-in db (conj (model/server-state-full-path state-path)
                                    :transaction-process/stage))]
     (if (empty? draft)
       {:db db
        :fx [(change-stage-fx {:process-id process-id
                               :old-stage  old-stage
                               :new-stage  new-stage
                               :ctx        ctx})]}
       {:db (update-in db state-path model/start-in-flight draft)
        :fx [(persist-draft-fx
              {:process-id  process-id
               :draft       draft
               :on-all-done (fn [success?]
                              (rf/dispatch [::save-draft-complete ctx success?])
                              (when success?
                                (rf/dispatch [::do-change-stage ctx new-stage])))})]}))))

(rf/reg-event-fx
 ::do-change-stage
 [rf/trim-v]
 (fn [{:keys [db]} [{:keys [state-path process-id] :as ctx} new-stage]]
   (let [old-stage (get-in db (conj (model/server-state-full-path state-path)
                                    :transaction-process/stage))]
     {:fx [(change-stage-fx {:process-id process-id
                             :old-stage  old-stage
                             :new-stage  new-stage
                             :ctx        ctx})]})))

(rf/reg-event-db
 ::change-stage-success
 [rf/trim-v]
 (fn [db [{:keys [state-path] :as _ctx} new-stage _resp]]
   (assoc-in db (conj (model/server-state-full-path state-path)
                      :transaction-process/stage)
             new-stage)))

(rf/reg-event-fx
 ::change-stage-error
 [rf/trim-v]
 (fn [{:keys [db]} [{:keys [state-path] :as _ctx} old-stage err]]
   (logging/error "Failed to change stage" err)
   {:db (assoc-in db (conj (model/server-state-full-path state-path)
                           :transaction-process/stage)
                  old-stage)
    :fx [[:dispatch [::show-save-error err]]]}))

;; Closing-stage terminal action. Persists pending fields (closing amount paid
;; + date) then flips the TP status to :completed to mark the transaction done.
(rf/reg-event-fx
 ::complete-transaction
 [rf/trim-v]
 (fn [{:keys [db]} [{:keys [state-path process-id] :as ctx} field-vals]]
   (let [draft-path (model/draft-full-path state-path)
         status-field {:transaction-process/status :transaction-process.status/completed}
         all-vals (merge field-vals status-field)
         db (update-in db draft-path merge all-vals)
         draft (get-in db draft-path)]
     {:db (update-in db state-path model/start-in-flight draft)
      :fx [(persist-draft-fx
            {:process-id  process-id
             :draft       draft
             :on-all-done (fn [success?]
                            (rf/dispatch [::save-draft-complete ctx success?])
                            (when success?
                              (rf/dispatch [::close-detail-panel ctx])
                              (open-notification!
                               :success
                               (loc/tr :transaction-process/transaction-completed))))})]})))

(rf/reg-event-fx
 ::reject-transaction
 [rf/trim-v]
 (fn [{:keys [db]} [{:keys [state-path process-id] :as ctx}]]
   (let [draft-path (model/draft-full-path state-path)
         status-field {:transaction-process/status :transaction-process.status/rejected}
         db (update-in db draft-path merge status-field)
         draft (get-in db draft-path)]
     {:db (update-in db state-path model/start-in-flight draft)
      :fx [(persist-draft-fx
            {:process-id  process-id
             :draft       draft
             :on-all-done (fn [success?]
                            (rf/dispatch [::save-draft-complete ctx success?])
                            (when success?
                              (rf/dispatch [::close-detail-panel ctx])
                              (open-notification!
                               :success
                               (loc/tr :transaction-process/transaction-rejected))))})]})))

;; Re-opens a terminal transaction: flips the TP status back to :active so the
;; closing-stage actions become available again. The stage is untouched.
(rf/reg-event-fx
 ::reopen-transaction
 [rf/trim-v]
 (fn [{:keys [db]} [{:keys [state-path process-id] :as ctx}]]
   (let [draft-path   (model/draft-full-path state-path)
         status-field {:transaction-process/status :transaction-process.status/active}
         db           (update-in db draft-path merge status-field)
         draft        (get-in db draft-path)]
     {:db (update-in db state-path model/start-in-flight draft)
      :fx [(persist-draft-fx
            {:process-id  process-id
             :draft       draft
             :on-all-done (fn [success?]
                            (rf/dispatch [::save-draft-complete ctx success?])
                            (when success?
                              (open-notification!
                               :success
                               (loc/tr :transaction-process/transaction-reopened))))})]})))

(rf/reg-event-fx
 ::delete-process-success
 [rf/trim-v]
 (fn [_cofx [process-id]]
   {:fx [[::sync-entities-to-session
          {:entities [{:db/id           process-id
                       :entity/type      :entity.type/transaction-process
                       :common/disabled? true}]}]
         [::navigate-to-grid nil]]}))

(rf/reg-event-fx
 ::delete-process
 [rf/trim-v]
 (fn [_cofx [{:keys [process-id] :as _ctx}]]
   {::disable-entity {:entity-id  process-id
                      :on-success (fn [_]
                                    (rf/dispatch [::delete-process-success process-id]))
                      :on-error   (fn [err]
                                    (rf/dispatch [::delete-sub-entity-failed err]))}}))

(rf/reg-event-db
 ::toggle-skip-mandatory-validation
 (fn [db]
   (update-in db model/skip-mandatory-validation-path not)))

(rf/reg-event-db
 ::set-impersonation
 [rf/trim-v]
 (fn [db [impersonation]]
   (assoc-in db model/impersonation-path impersonation)))

;; UI state events

(rf/reg-event-db
 ::set-show-permissions
 [rf/trim-v]
 (fn [db [{:keys [state-path] :as _ctx} show?]]
   (assoc-in db (model/show-permissions-full-path state-path) show?)))

(rf/reg-event-db
 ::set-show-details
 [rf/trim-v]
 (fn [db [{:keys [state-path] :as _ctx} show?]]
   (assoc-in db (model/show-details-full-path state-path) show?)))

(rf/reg-event-db
 ::toggle-sidebar
 [rf/trim-v]
 (fn [db [{:keys [state-path] :as _ctx}]]
   (update-in db (model/sidebar-collapsed-full-path state-path) not)))

(rf/reg-event-db
 ::set-detail-panel
 [rf/trim-v]
 (fn [db [{:keys [state-path] :as _ctx} panel-data]]
   (assoc-in db (model/detail-panel-full-path state-path) panel-data)))

(rf/reg-event-db
 ::close-detail-panel
 [rf/trim-v]
 (fn [db [{:keys [state-path] :as _ctx}]]
   (assoc-in db (model/detail-panel-full-path state-path) nil)))

(rf/reg-event-db
 ::set-modal
 [rf/trim-v]
 (fn [db [{:keys [state-path] :as _ctx} modal-data]]
   (assoc-in db (model/modal-full-path state-path) modal-data)))

(rf/reg-event-db
 ::close-modal
 [rf/trim-v]
 (fn [db [{:keys [state-path] :as _ctx}]]
   (-> db
       (assoc-in (model/modal-full-path state-path) nil)
       (update-in (model/ui-full-path state-path) dissoc :modal-form))))

;; Sub-entity actions (used by detail panel)

(rf/reg-event-fx
 ::mark-condition-as-met
 [rf/trim-v]
 (fn [_cofx [condition-id status-attr]]
   (let [ns-str  (namespace status-attr)
         true-kw (keyword (str ns-str ".status") "true")]
     {::field-update-single {:entity-id condition-id
                             :attr      status-attr
                             :value     true-kw}})))

(rf/reg-fx
 ::field-update-single
 (fn [{:keys [entity-id attr value]}]
   (entity-actions/optimistic-field-update
    entity-id attr value
    session/entities
    (fn [_] nil)
    (fn [err] (logging/error "Failed to update" attr err)))))

(rf/reg-event-fx
 ::delete-sub-entity-failed
 [rf/trim-v]
 (fn [_ [err]]
   (logging/error "Failed to delete entity" err)
   {:fx [[:dispatch
          [:open-notification
           {:title       (loc/tr :transaction-process/delete-failed)
            :type        :error
            :auto-close? true}]]]}))

(rf/reg-event-fx
 ::delete-sub-entity
 [rf/trim-v]
 (fn [_cofx [{:keys [process-id field-kw entity-id on-success]}]]
   {::remove-sub-entity {:process-id process-id
                         :field-kw   field-kw
                         :entity-id  entity-id
                         :on-success (fn [_] (when on-success (on-success)))
                         :on-error   (fn [err]
                                       (rf/dispatch [::delete-sub-entity-failed err]))}}))

(rf/reg-fx
 ::remove-sub-entity
 (fn [{:keys [process-id field-kw entity-id on-success on-error]}]
   (-> (conn/sendp! :transaction-process/remove-sub-entity
                    {:process-id process-id
                     :field-kw   field-kw
                     :entity-id  entity-id})
       (p/then  (fn [resp] (when on-success (on-success resp))))
       (p/catch (fn [err]  (when on-error  (on-error err)))))))
